import { useEffect, memo, useMemo } from 'react';
import { nanoid } from '@reduxjs/toolkit';
import { buildEmptyTargetImage, getEditorEmptyStateMode, getEmptyStateUploadTargetIndex, isFirstTargetImage, useSetState } from 'utils/functions.utils';
import {
  AddDropdown,
  EditorWorkspace,
  Colors,
  Main,
  Properties,
  Models,
  Loader,
  Assets,
} from 'utils/imports.utils';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, persistor, RootState } from 'store/store';
import { setSelectedTargetImageID, setSelectedObjectID, setCurrentSelectedPropertyType, setSelectedTargetIndex, setShouldUpdateObjectState, addTargetImage, setZoomPercentage, resetZoomPercentage, undo, redo, markSaved, loadProjectData, clearHistory } from 'store/reducers/editorSlice';
import { PersistGate } from 'redux-persist/integration/react';
import { ProjectStatus, PropertiesTypes } from 'utils/interface.utils';
import EditorEmptyState from 'components/editor_empty_state/editor_empty_state.component';
import ZoomPill from 'components/zoom_pill/zoom_pill.component';
import EditorNextStep from 'components/editor_next_step/editor_next_step.component';
import PropertiesEmptyState from 'components/properties_empty_state/properties_empty_state.component';
import EditorCanvasNote from 'components/editor_canvas_note/editor_canvas_note.component';
import TransformToolbar from 'components/transform_toolbar/transform_toolbar.component';
import './editor.screen.scss';

const EditorPersistenceLoading = () => (
  <Main
    backgroundColor={Colors.editorBackground}
    isEditor={true}
    isRenderOnlyLogo={false}
  >
    <div className="editor_loader_container">
      <Loader icon={Assets.loader_2} width="32px" height="32px" />
    </div>
  </Main>
)

const Editor = () => {
  const [state, setState] = useSetState({
    isPropertiesOpen: false,
    projectId: null,
    isLoadingProjectData: false,
    isProjectDataLoaded: false,
    uploadRequestCount: 0,
    addObjectRequestCount: 0,
    isNextStepOpen: false,
    projectStatus:'',
    projectPaymentStatus:false,
  });

  const dispatch = useDispatch<AppDispatch>();

  const targetImageDataList = useSelector(
    (state: RootState) => state.editor.targetImageDataList,
    shallowEqual
  );

  const { selectedTargetIndex, currentSelectedPropertyType, zoomPercentage } = useSelector((state: RootState) => state.editor);
  const currentProject = useSelector((state: RootState) => state.project.currentProject);

  const currentTargetImage = useMemo(() => {
    if (selectedTargetIndex !== null && targetImageDataList[selectedTargetIndex]) {
      return targetImageDataList[selectedTargetIndex].targetImage;
    }
    return null;
  }, [targetImageDataList, selectedTargetIndex]);

  const emptyStateMode = getEditorEmptyStateMode(targetImageDataList, selectedTargetIndex);
  // An open project starts with an empty target list until its data is fetched; holding the card
  // back until then stops it flashing up before the loader on a project that does have targets.
  const isAwaitingProjectData = !!currentProject && !state.isProjectDataLoaded;
  // Same gate as the Add dropdown's "+" for targets.
  const canAddTarget = !state.projectPaymentStatus && state.projectStatus !== ProjectStatus.PUBLISHED;

  const openPropertiesCallback = (isOpen: boolean) => {
    setState({ isPropertiesOpen: isOpen });
  };

  // The "Make this picture pop" card's button: the card goes away and the Add dropdown opens its object chooser.
  const handleAddObjectRequest = () => {
    setState({ isNextStepOpen: false, addObjectRequestCount: state.addObjectRequestCount + 1 });
  };

  // The Objects "+": for a target's first object the card comes first; every later object goes straight to the chooser.
  const handleObjectsPlusClick = () => {
    const isFirstObject = (targetImageDataList[selectedTargetIndex ?? 0]?.objects.length ?? 0) === 0;
    if (isFirstObject) {
      setState({ isNextStepOpen: true });
      return;
    }
    handleAddObjectRequest();
  };

  const handleEmptyStateAction = () => {
    const uploadTargetIndex = getEmptyStateUploadTargetIndex(targetImageDataList, selectedTargetIndex);

    // No targets yet: mirrors the Add dropdown's "+" for targets. Properties opens on the new,
    // image-less target and its own effect then opens the upload dialog.
    if (uploadTargetIndex === null) {
      const emptyTarget = buildEmptyTargetImage(nanoid());
      dispatch(setCurrentSelectedPropertyType(PropertiesTypes.TARGET));
      dispatch(addTargetImage(emptyTarget));
      dispatch(setSelectedTargetIndex(targetImageDataList.length));
      dispatch(setSelectedTargetImageID(emptyTarget.id));
      setState({ isPropertiesOpen: true });
      return;
    }

    // A target is already there (e.g. the upload dialog was closed without uploading): point the
    // selection at it and ask Properties to open the dialog again, rather than adding another.
    dispatch(setSelectedTargetIndex(uploadTargetIndex));
    dispatch(setSelectedTargetImageID(targetImageDataList[uploadTargetIndex].id));
    dispatch(setSelectedObjectID(null));
    dispatch(setCurrentSelectedPropertyType(PropertiesTypes.TARGET));
    setState({ isPropertiesOpen: true, uploadRequestCount: state.uploadRequestCount + 1 });
  };

  const getManyProjectData = async () => {
    if (!state.projectId) return;
    try {
      setState({ isLoadingProjectData: true })
      const res: any = await Models.auth.getManyProjectData({
        project_id: state.projectId,
      });
      if (res && res.data) {
        const targets = res.data.targets.map((target: any) => {
          return {
            ...target.data,
            isPaid: target.paid,
            isLocked: target.locked,
            projectDataId: target._id,
          }
        })
        if (targets.length > 0) {
          dispatch(loadProjectData(targets));
          dispatch(setSelectedTargetIndex(0))
          dispatch(setSelectedTargetImageID(targets[0].id))
          const firstObject = targets[0].objects[0];
          dispatch(setCurrentSelectedPropertyType(firstObject ? firstObject.type : PropertiesTypes.TARGET))
          if (firstObject) {
            dispatch(setSelectedObjectID(firstObject.id))
          }
          dispatch(setShouldUpdateObjectState(true))
          setState({ isPropertiesOpen: true })
        }
        // Freshly loaded data matches the server; nothing is unsaved yet.
        dispatch(markSaved());
        setState({ isLoadingProjectData: false, isProjectDataLoaded: true })
      } else {
        console.error('No project data found');
        setState({ isLoadingProjectData: false, isProjectDataLoaded: true })
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
      setState({ isLoadingProjectData: false, isProjectDataLoaded: true })
    }
  };

  const getCurrentProjectData = async () => {
    if (!state.projectId) return;
    try {
      const res: any = await Models.auth.getProject({ project_id: state.projectId });
      if (res && res.data) {
        setState({ projectStatus: res.data.project_status, projectPaymentStatus: res.data.payment_status })
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  }

  useEffect(() => {
    getManyProjectData();
    getCurrentProjectData();
  }, [state.projectId]);

  useEffect(() => {
    if (currentProject) {
      setState({ projectId: currentProject.project_id });
    }
  }, [currentProject]);


  // Undo history belongs to this editing session; leaving by any route must not carry it into the next project.
  useEffect(() => {
    return () => { dispatch(clearHistory()); };
  }, [dispatch])

  useEffect(() => {
    if (currentSelectedPropertyType) {
      setState({ isPropertiesOpen: true })
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTypingInField = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if (isTypingInField) return;

      const isModifierPressed = event.metaKey || event.ctrlKey;
      if (!isModifierPressed) return;

      const key = event.key.toLowerCase();
      if (key === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          dispatch(redo());
        } else {
          dispatch(undo());
        }
      } else if (key === 'y') {
        event.preventDefault();
        dispatch(redo());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch])

  return (
    <PersistGate loading={<EditorPersistenceLoading />} persistor={persistor}>
      <Main
        backgroundColor={Colors.editorBackground}
        isEditor={true}
        isRenderOnlyLogo={false}>
        <div className="editor">
          {state.isLoadingProjectData ?
            <div className="editor_loader_container">
              <Loader icon={Assets.loader_2} width="32px" height="32px" />
            </div>
            :
            <div className="editor_container">
              <div className="editor_add_dropdown_container">
                <AddDropdown openProperties={openPropertiesCallback} projectPaymentStatus={state.projectPaymentStatus} projectStatus={state.projectStatus} addObjectRequestCount={state.addObjectRequestCount} onAddObjectClick={handleObjectsPlusClick} />
              </div>
              {currentTargetImage && selectedTargetIndex !== null && (
                <div className="editor_workspace_container">
                  <EditorWorkspace />
                  <TransformToolbar />
                  <EditorCanvasNote />
                  <div className="editor_zoom_slot">
                    <ZoomPill value={zoomPercentage} onChange={(zoom) => dispatch(setZoomPercentage(zoom))} onReset={() => dispatch(resetZoomPercentage())} />
                  </div>
                  {state.isNextStepOpen && (
                    <div className="editor_next_step_slot">
                      <EditorNextStep onAddObject={handleAddObjectRequest} />
                    </div>
                  )}
                </div>
              )}
              {emptyStateMode !== 'hidden' && !isAwaitingProjectData && (
                <div className="editor_empty_state_slot">
                  <EditorEmptyState isFirstTarget={isFirstTargetImage(targetImageDataList)} canAddTarget={canAddTarget} onAddTarget={handleEmptyStateAction} />
                </div>
              )}
              {emptyStateMode === 'addTarget' && !isAwaitingProjectData && (
                <div className="editor_properties_container">
                  <PropertiesEmptyState />
                </div>
              )}
              {targetImageDataList.length > 0 && state.isPropertiesOpen && (
                <div className="editor_properties_container">
                  <Properties projectPaymentStatus={state.projectPaymentStatus} projectStatus={state.projectStatus} uploadRequestCount={state.uploadRequestCount} />
                </div>
              )}
            </div>
          }
        </div>
      </Main>
    </PersistGate>
  );
};

export default memo(Editor);
